/**
 * Recipient Search Types
 *
 * Type definitions for the recipient search feature.
 * These align with the backend API schemas in server_v2/v1/schemas/recipient_search.py
 */

// --- Identifier Search Types ---

export type IdentifierType = 'uei' | 'duns' | 'cage'
export type SearchMode = 'text' | 'uei' | 'duns' | 'cage'

// Token pill colors for identifier type badges
export const IDENTIFIER_COLORS = {
  uei: {
    bg: 'bg-purple-500/20',
    text: 'text-purple-400',
    border: 'border-purple-500/30',
    darkBg: 'dark:bg-purple-500/20',
    darkText: 'dark:text-purple-300',
    darkBorder: 'dark:border-purple-500/30',
  },
  duns: {
    bg: 'bg-cyan-500/20',
    text: 'text-cyan-400',
    border: 'border-cyan-500/30',
    darkBg: 'dark:bg-cyan-500/20',
    darkText: 'dark:text-cyan-300',
    darkBorder: 'dark:border-cyan-500/30',
  },
  cage: {
    bg: 'bg-orange-500/20',
    text: 'text-orange-400',
    border: 'border-orange-500/30',
    darkBg: 'dark:bg-orange-500/20',
    darkText: 'dark:text-orange-300',
    darkBorder: 'dark:border-orange-500/30',
  },
} as const

// --- Filter Types ---

export interface RecipientSearchFilters {
  // Text search
  query?: string

  // Identifier filters
  uei?: string
  duns?: string
  cage_code?: string

  // Location filters
  states: string[]
  cities: string[]
  zip_codes: string[]
  congressional_districts: string[]

  // Socioeconomic certifications
  woman_owned?: boolean
  veteran_owned?: boolean
  sdvosb?: boolean
  is_8a?: boolean
  hubzone?: boolean
  sdb?: boolean
  minority_owned?: boolean
  small_business?: boolean
  alaska_native?: boolean
  native_american?: boolean
  nonprofit?: boolean
  educational?: boolean

  // Industry filters
  naics_codes: string[]
  psc_codes: string[]
  agencies: string[]
  set_asides: string[]
  fiscal_years: number[]

  // Financial filters (values in dollars)
  min_spending?: number
  max_spending?: number
  min_awards?: number
  max_awards?: number

  // Date filters
  min_last_award_date?: string
  max_last_award_date?: string

  // Parent/subsidiary filters
  parent_recipient_hash?: string
  has_parent?: boolean

  // Sorting & pagination
  sort: RecipientSortOption
  limit: number
  cursor?: string

  // Identifier search mode
  identifier_type?: IdentifierType
}

export type RecipientSortOption =
  | 'relevance'
  | 'spending_desc'
  | 'spending_asc'
  | 'awards_desc'
  | 'recent_activity'
  | 'name_asc'

// --- Result Types ---

export interface RecipientSearchResult {
  recipient_hash: string
  recipient_id: string
  recipient_level: string | null
  name: string | null
  alternate_names: string[] | null
  uei: string | null
  duns: string | null
  cage_code: string | null

  // Location
  state: string | null
  city: string | null
  zip5: string | null
  congressional_district: string | null
  country_code: string | null

  // Socioeconomic flags
  woman_owned: boolean | null
  veteran_owned: boolean | null
  sdvosb: boolean | null
  is_8a: boolean | null
  hubzone: boolean | null
  sdb: boolean | null
  minority_owned: boolean | null
  small_business: boolean | null
  alaska_native: boolean | null
  native_american: boolean | null
  nonprofit: boolean | null
  educational: boolean | null

  // Financial (returned in dollars)
  last_12_months: number | null
  last_12_contracts: number | null
  last_12_grants: number | null
  last_12_direct_payments: number | null
  last_12_loans: number | null
  last_12_other: number | null
  last_12_award_count: number | null
  /** @deprecated use last_12_award_count */
  award_count: number | null
  total_awards: number | null
  total_amount: number | null

  // Industry
  naics_codes: string[] | null
  psc_codes: string[] | null
  agencies: string[] | null

  // Dates
  first_award_date: string | null
  last_award_date: string | null

  // Parent info
  parent_recipient_hash: string | null
  parent_name: string | null
  has_parent: boolean
  subsidiary_count: number
}

export interface RecipientSearchResponse {
  results: RecipientSearchResult[]
  next_cursor: string | null
  has_more: boolean
  limit: number
  search_mode: SearchMode
  limit_reached?: boolean
}

export interface RecipientAutocompleteResult {
  recipient_hash: string
  recipient_id?: string | null
  recipient_level?: string | null
  name: string | null
  uei: string | null
  state: string | null
  city: string | null
  subsidiary_count?: number
}

export interface RecipientCountResponse {
  count: number
}

// --- Certification Badge Types ---

export type CertificationType =
  | 'woman_owned'
  | 'veteran_owned'
  | 'sdvosb'
  | 'is_8a'
  | 'hubzone'
  | 'sdb'
  | 'minority_owned'
  | 'small_business'
  | 'alaska_native'
  | 'native_american'
  | 'nonprofit'
  | 'educational'

export interface CertificationBadgeConfig {
  type: CertificationType
  label: string
  shortLabel: string
  color: string
  darkColor: string
}

// --- Default Values ---

export const DEFAULT_RECIPIENT_FILTERS: RecipientSearchFilters = {
  states: [],
  cities: [],
  zip_codes: [],
  congressional_districts: [],
  naics_codes: [],
  psc_codes: [],
  agencies: [],
  set_asides: [],
  fiscal_years: [],
  sort: 'relevance',
  limit: 20,
}

export const CERTIFICATION_BADGES: CertificationBadgeConfig[] = [
  {
    type: 'is_8a',
    label: '8(a) Program',
    shortLabel: '8(a)',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    darkColor:
      'dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700',
  },
  {
    type: 'hubzone',
    label: 'HUBZone',
    shortLabel: 'HUBZone',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    darkColor: 'dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700',
  },
  {
    type: 'sdvosb',
    label: 'Service-Disabled Veteran',
    shortLabel: 'SDVOSB',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    darkColor:
      'dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700',
  },
  {
    type: 'woman_owned',
    label: 'Woman-Owned',
    shortLabel: 'WOSB',
    color: 'bg-pink-100 text-pink-800 border-pink-200',
    darkColor: 'dark:bg-pink-900/40 dark:text-pink-300 dark:border-pink-700',
  },
  {
    type: 'veteran_owned',
    label: 'Veteran-Owned',
    shortLabel: 'VOSB',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    darkColor: 'dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700',
  },
  {
    type: 'sdb',
    label: 'Small Disadvantaged',
    shortLabel: 'SDB',
    color: 'bg-teal-100 text-teal-800 border-teal-200',
    darkColor: 'dark:bg-teal-900/40 dark:text-teal-300 dark:border-teal-700',
  },
  {
    type: 'minority_owned',
    label: 'Minority-Owned',
    shortLabel: 'MBE',
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    darkColor:
      'dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-700',
  },
  {
    type: 'small_business',
    label: 'Small Business',
    shortLabel: 'SB',
    color: 'bg-slate-100 text-slate-800 border-slate-200',
    darkColor: 'dark:bg-slate-800/40 dark:text-slate-300 dark:border-slate-600',
  },
  {
    type: 'alaska_native',
    label: 'Alaska Native Corporation',
    shortLabel: 'ANC',
    color: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    darkColor: 'dark:bg-cyan-900/40 dark:text-cyan-300 dark:border-cyan-700',
  },
  {
    type: 'native_american',
    label: 'Native American Owned',
    shortLabel: 'NAO',
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    darkColor:
      'dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-700',
  },
  {
    type: 'nonprofit',
    label: 'Nonprofit Organization',
    shortLabel: 'NPO',
    color: 'bg-green-100 text-green-800 border-green-200',
    darkColor: 'dark:bg-green-900/40 dark:text-green-300 dark:border-green-700',
  },
  {
    type: 'educational',
    label: 'Educational Institution',
    shortLabel: 'EDU',
    color: 'bg-violet-100 text-violet-800 border-violet-200',
    darkColor:
      'dark:bg-violet-900/40 dark:text-violet-300 dark:border-violet-700',
  },
]

export type SortIconType = 'dollar' | 'hash' | 'clock' | 'text'

export const SORT_OPTIONS: {
  value: RecipientSortOption
  label: string
  icon?: SortIconType
}[] = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'spending_desc', label: 'Most Awarded', icon: 'dollar' },
  { value: 'spending_asc', label: 'Least Awarded', icon: 'dollar' },
  { value: 'awards_desc', label: 'Most Awards (12 Mo)', icon: 'hash' },
  { value: 'recent_activity', label: 'Recent Activity', icon: 'clock' },
  { value: 'name_asc', label: 'Name (A-Z)', icon: 'text' },
]

// --- Export Types ---

export type RecipientExportFormat = 'xlsx' | 'csv'
